import { create } from "zustand";
import { v4 as uuidv4 } from "uuid";
import { AppState, Token, CounterData } from "../types";
import { isConfigured, getConfig, checkProxyConnection } from "./cloudflareApi";
import {
  saveTokensToCloudflare,
  saveCountersToCloudflare,
  saveMarketingColumnsToCloudflare,
  loadAllFromCloudflare,
} from "./cloudflareStore";

// Initial default data if nothing is loaded
const defaultData = {
  tokens: [],
  counters: { MarketingA: {}, MarketingB: {} },
  marketingColumns: ["MarketingA", "MarketingB"],
  selectedTokenIds: [],
};

// Debug flag - enables detailed logging
const DEBUG = true;

// Calculate counters from tokens data
const calculateCounters = (
  tokens: Token[],
  marketingColumns: string[]
): CounterData => {
  if (DEBUG) console.log(`Calculating counters for ${tokens.length} tokens`);

  const counters: CounterData = {};

  // Initialize counters for each marketing column
  marketingColumns.forEach((column) => {
    counters[column] = {};
  });

  // Count occurrences of each marketing entry
  tokens.forEach((token) => {
    marketingColumns.forEach((column) => {
      const entries = token[column];
      if (Array.isArray(entries)) {
        entries.forEach((entry) => {
          if (!counters[column][entry]) {
            counters[column][entry] = 0;
          }
          counters[column][entry]++;
        });
      }
    });
  });

  return counters;
};

// Calculate counters for selected tokens only
const calculateFilteredCounters = (
  tokens: Token[],
  selectedTokenIds: string[],
  marketingColumns: string[]
): CounterData => {
  if (selectedTokenIds.length === 0) {
    return calculateCounters(tokens, marketingColumns); // Return all counters if no selection
  }

  const selectedTokens = tokens.filter((token) =>
    selectedTokenIds.includes(token.id)
  );
  return calculateCounters(selectedTokens, marketingColumns);
};

// Create the store
export const useStore = create<AppState>((set, get) => {
  // Initialize with default values
  const initialState = {
    ...defaultData,
    activeFilter: null,
    sortColumn: null,
    sortDirection: null,
  };

  // Attempt to load data from Cloudflare KV on initialization
  // This happens asynchronously after the store is created
  const loadInitialData = async () => {
    console.log("🔄 Attempting to load initial data...");

    if (isConfigured()) {
      console.log(
        "✅ Cloudflare KV is configured, checking proxy connection..."
      );

      // First, check if the proxy server is available
      const proxyAvailable = await checkProxyConnection();
      if (!proxyAvailable) {
        console.error(
          "❌ Proxy server is not available. Cannot load data from Cloudflare KV."
        );
        console.log("⚠️ Using default empty state instead.");
        return;
      }

      console.log(
        "✅ Proxy server is available. Loading data from Cloudflare KV..."
      );

      try {
        // Attempt to load data with retries
        const maxRetries = 3;
        let attempt = 0;
        let success = false;
        let data: {
          tokens: Token[] | null;
          counters: CounterData | null;
          marketingColumns: string[] | null;
        } | null = null;

        while (attempt < maxRetries && !success) {
          attempt++;
          console.log(`🔄 Loading data attempt ${attempt}/${maxRetries}`);

          try {
            const config = getConfig();
            console.log(
              `🔍 Using config with proxyUrl: ${config?.proxyUrl || "not set"}`
            );

            data = await loadAllFromCloudflare();

            console.log("📊 Received data:", {
              tokens: data?.tokens ? `${data.tokens.length} items` : "null",
              marketingColumns: data?.marketingColumns
                ? `${data.marketingColumns.length} items`
                : "null",
              counters: data?.counters ? "present" : "null",
            });

            if (data?.tokens && data?.marketingColumns) {
              success = true;
            } else {
              console.warn(
                "⚠️ Incomplete data received from Cloudflare KV, retrying..."
              );

              // Log what was missing
              if (!data) {
                console.warn("⚠️ No data received at all");
              } else {
                if (!data.tokens) console.warn("⚠️ No tokens received");
                if (!data.marketingColumns)
                  console.warn("⚠️ No marketing columns received");
              }

              // Wait before retrying
              const waitTime = 1000 * attempt; // Progressive backoff
              console.log(`⏳ Waiting ${waitTime}ms before retry...`);
              await new Promise((resolve) => setTimeout(resolve, waitTime));
            }
          } catch (error) {
            console.error(`❌ Error on attempt ${attempt}:`, error);
            // Wait before retrying
            const waitTime = 1000 * attempt; // Progressive backoff
            console.log(`⏳ Waiting ${waitTime}ms before retry...`);
            await new Promise((resolve) => setTimeout(resolve, waitTime));
          }
        }

        if (success && data && data.tokens && data.marketingColumns) {
          console.log("✅ Successfully loaded data from Cloudflare KV:", {
            tokensCount: data.tokens.length || 0,
            columnsCount: data.marketingColumns.length || 0,
          });

          set({
            tokens: data.tokens,
            marketingColumns: data.marketingColumns,
            counters:
              data.counters ||
              calculateCounters(data.tokens, data.marketingColumns),
          });

          // Verify data was set correctly
          setTimeout(() => {
            const state = get();
            console.log("🔍 Verifying state after data load:", {
              tokensCount: state.tokens.length,
              columnsCount: state.marketingColumns.length,
            });
          }, 100);
        } else {
          console.warn(
            "⚠️ Failed to load data after multiple attempts, using default empty state"
          );
          // Set empty state explicitly here rather than relying on initial state
          set({
            tokens: [],
            marketingColumns: ["MarketingA", "MarketingB"],
            counters: { MarketingA: {}, MarketingB: {} },
          });
        }
      } catch (error) {
        console.error("❌ Error loading data from Cloudflare KV:", error);
        // Set empty state explicitly here rather than relying on initial state
        set({
          tokens: [],
          marketingColumns: ["MarketingA", "MarketingB"],
          counters: { MarketingA: {}, MarketingB: {} },
        });
      }
    } else {
      console.warn(
        "⚠️ Cloudflare KV not configured. Using default empty state."
      );

      // Log more details about why it's not configured
      const config = getConfig();
      if (!config) {
        console.warn("⚠️ getConfig() returned null");
      }

      // Set empty state explicitly here rather than relying on initial state
      set({
        tokens: [],
        marketingColumns: ["MarketingA", "MarketingB"],
        counters: { MarketingA: {}, MarketingB: {} },
      });
    }
  };

  // Start loading initial data
  loadInitialData();

  // Add a method to manually trigger data loading (for debugging/retry)
  const manuallyLoadData = async () => {
    console.log("🔄 Manually triggering data load...");
    await loadInitialData();
    return true;
  };

  return {
    ...initialState,
    manuallyLoadData,

    // Method to add a token
    addToken: (token: Token) => {
      // Create new token with ID
      const newToken = {
        ...token,
        id: uuidv4(),
      };

      // Add to state
      set((state) => {
        const newTokens = [...state.tokens, newToken];
        const newCounters = calculateCounters(
          newTokens,
          state.marketingColumns
        );

        // Save to Cloudflare KV
        if (isConfigured()) {
          saveTokensToCloudflare(newTokens)
            .then((success) => {
              if (!success) {
                console.error("Failed to save tokens to Cloudflare KV");
              }
            })
            .catch((error) => {
              console.error("Error saving tokens to Cloudflare KV:", error);
            });

          saveCountersToCloudflare(newCounters).catch((error) => {
            console.error("Error saving counters to Cloudflare KV:", error);
          });
        }

        return {
          tokens: newTokens,
          counters: newCounters,
        };
      });
    },

    // Method to edit a token
    editToken: (token: Token) => {
      set((state) => {
        const index = state.tokens.findIndex((t) => t.id === token.id);
        if (index === -1) return state;

        const newTokens = [...state.tokens];
        newTokens[index] = token;
        const newCounters = calculateCounters(
          newTokens,
          state.marketingColumns
        );

        // Save to Cloudflare KV
        if (isConfigured()) {
          saveTokensToCloudflare(newTokens)
            .then((success) => {
              if (!success) {
                console.error("Failed to save tokens to Cloudflare KV");
              }
            })
            .catch((error) => {
              console.error("Error saving tokens to Cloudflare KV:", error);
            });

          saveCountersToCloudflare(newCounters).catch((error) => {
            console.error("Error saving counters to Cloudflare KV:", error);
          });
        }

        return {
          tokens: newTokens,
          counters: newCounters,
        };
      });
    },

    // Method to remove a token
    removeToken: (tokenId: string) => {
      set((state) => {
        const newTokens = state.tokens.filter((token) => token.id !== tokenId);
        const newCounters = calculateCounters(
          newTokens,
          state.marketingColumns
        );

        // Save to Cloudflare KV
        if (isConfigured()) {
          saveTokensToCloudflare(newTokens)
            .then((success) => {
              if (!success) {
                console.error("Failed to save tokens to Cloudflare KV");
              }
            })
            .catch((error) => {
              console.error("Error saving tokens to Cloudflare KV:", error);
            });

          saveCountersToCloudflare(newCounters).catch((error) => {
            console.error("Error saving counters to Cloudflare KV:", error);
          });
        }

        return {
          tokens: newTokens,
          counters: newCounters,
          selectedTokenIds: state.selectedTokenIds.filter(
            (id) => id !== tokenId
          ),
        };
      });
    },

    // Method to add a marketing column
    addMarketingColumn: (columnName: string) => {
      set((state) => {
        if (state.marketingColumns.includes(columnName)) return state;

        const newMarketingColumns = [...state.marketingColumns, columnName];
        const newCounters = { ...state.counters, [columnName]: {} };

        // Save to Cloudflare KV
        if (isConfigured()) {
          saveMarketingColumnsToCloudflare(newMarketingColumns)
            .then((success) => {
              if (!success) {
                console.error(
                  "Failed to save marketing columns to Cloudflare KV"
                );
              }
            })
            .catch((error) => {
              console.error(
                "Error saving marketing columns to Cloudflare KV:",
                error
              );
            });

          saveCountersToCloudflare(newCounters).catch((error) => {
            console.error("Error saving counters to Cloudflare KV:", error);
          });
        }

        return {
          marketingColumns: newMarketingColumns,
          counters: newCounters,
        };
      });
    },

    // Method to add multiple marketing columns
    addMultipleMarketingColumns: (columnNames: string[]) => {
      set((state) => {
        const newColumns = columnNames.filter(
          (name) => !state.marketingColumns.includes(name)
        );
        if (newColumns.length === 0) return state;

        const newMarketingColumns = [...state.marketingColumns, ...newColumns];
        const newCounters = { ...state.counters };

        // Initialize counters for new columns
        newColumns.forEach((column) => {
          newCounters[column] = {};
        });

        // Save to Cloudflare KV
        if (isConfigured()) {
          saveMarketingColumnsToCloudflare(newMarketingColumns)
            .then((success) => {
              if (!success) {
                console.error(
                  "Failed to save marketing columns to Cloudflare KV"
                );
              }
            })
            .catch((error) => {
              console.error(
                "Error saving marketing columns to Cloudflare KV:",
                error
              );
            });

          saveCountersToCloudflare(newCounters).catch((error) => {
            console.error("Error saving counters to Cloudflare KV:", error);
          });
        }

        return {
          marketingColumns: newMarketingColumns,
          counters: newCounters,
        };
      });
    },

    // Method to import tokens
    importTokens: (newTokens: Token[], append = true) => {
      set((state) => {
        let combinedTokens: Token[];

        if (append) {
          // Create a map of existing tokens by ID
          const existingTokensMap = new Map(
            state.tokens.map((token) => [token.id, token])
          );

          // Add or overwrite tokens from newTokens
          newTokens.forEach((token) => {
            existingTokensMap.set(token.id, token);
          });

          // Convert map back to array
          combinedTokens = Array.from(existingTokensMap.values());
        } else {
          // Replace all tokens
          combinedTokens = newTokens;
        }

        // Find any new marketing columns from imported tokens
        const allColumns = new Set<string>(state.marketingColumns);

        for (const token of combinedTokens) {
          for (const key in token) {
            if (
              key !== "id" &&
              key !== "TokenName" &&
              key !== "TokenWebsiteTraffic" &&
              key !== "TokenTotalRaised" &&
              key !== "Telegram" &&
              key !== "X" &&
              key !== "Instagram" &&
              !allColumns.has(key)
            ) {
              allColumns.add(key);
            }
          }
        }

        const newMarketingColumns = Array.from(allColumns);
        const newCounters = calculateCounters(
          combinedTokens,
          newMarketingColumns
        );

        // Save to Cloudflare KV
        if (isConfigured()) {
          saveTokensToCloudflare(combinedTokens)
            .then((success) => {
              if (!success) {
                console.error("Failed to save tokens to Cloudflare KV");
              }
            })
            .catch((error) => {
              console.error("Error saving tokens to Cloudflare KV:", error);
            });

          saveMarketingColumnsToCloudflare(newMarketingColumns)
            .then((success) => {
              if (!success) {
                console.error(
                  "Failed to save marketing columns to Cloudflare KV"
                );
              }
            })
            .catch((error) => {
              console.error(
                "Error saving marketing columns to Cloudflare KV:",
                error
              );
            });

          saveCountersToCloudflare(newCounters).catch((error) => {
            console.error("Error saving counters to Cloudflare KV:", error);
          });
        }

        return {
          tokens: combinedTokens,
          marketingColumns: newMarketingColumns,
          counters: newCounters,
        };
      });
    },

    // Method to edit a marketing column
    editMarketingColumn: (oldName: string, newName: string) => {
      set((state) => {
        const index = state.marketingColumns.indexOf(oldName);
        if (index === -1 || state.marketingColumns.includes(newName))
          return state;

        // Update marketing columns
        const newMarketingColumns = [...state.marketingColumns];
        newMarketingColumns[index] = newName;

        // Update counters
        const newCounters = { ...state.counters };
        newCounters[newName] = newCounters[oldName];
        delete newCounters[oldName];

        // Update tokens
        const newTokens = state.tokens.map((token) => {
          const newToken = { ...token };
          if (Array.isArray(token[oldName])) {
            newToken[newName] = token[oldName];
            delete newToken[oldName];
          }
          return newToken;
        });

        // Save to Cloudflare KV
        if (isConfigured()) {
          saveTokensToCloudflare(newTokens)
            .then((success) => {
              if (!success) {
                console.error("Failed to save tokens to Cloudflare KV");
              }
            })
            .catch((error) => {
              console.error("Error saving tokens to Cloudflare KV:", error);
            });

          saveMarketingColumnsToCloudflare(newMarketingColumns)
            .then((success) => {
              if (!success) {
                console.error(
                  "Failed to save marketing columns to Cloudflare KV"
                );
              }
            })
            .catch((error) => {
              console.error(
                "Error saving marketing columns to Cloudflare KV:",
                error
              );
            });

          saveCountersToCloudflare(newCounters).catch((error) => {
            console.error("Error saving counters to Cloudflare KV:", error);
          });
        }

        return {
          marketingColumns: newMarketingColumns,
          counters: newCounters,
          tokens: newTokens,
          activeFilter:
            state.activeFilter?.column === oldName
              ? { column: newName, entry: state.activeFilter.entry }
              : state.activeFilter,
        };
      });
    },

    // Method to remove a marketing column
    removeMarketingColumn: (columnName: string) => {
      set((state) => {
        const index = state.marketingColumns.indexOf(columnName);
        if (index === -1) return state;

        // Update marketing columns
        const newMarketingColumns = state.marketingColumns.filter(
          (col) => col !== columnName
        );

        // Update counters
        const newCounters = { ...state.counters };
        delete newCounters[columnName];

        // Update tokens
        const newTokens = state.tokens.map((token) => {
          const newToken = { ...token };
          delete newToken[columnName];
          return newToken;
        });

        // Save to Cloudflare KV
        if (isConfigured()) {
          saveTokensToCloudflare(newTokens)
            .then((success) => {
              if (!success) {
                console.error("Failed to save tokens to Cloudflare KV");
              }
            })
            .catch((error) => {
              console.error("Error saving tokens to Cloudflare KV:", error);
            });

          saveMarketingColumnsToCloudflare(newMarketingColumns)
            .then((success) => {
              if (!success) {
                console.error(
                  "Failed to save marketing columns to Cloudflare KV"
                );
              }
            })
            .catch((error) => {
              console.error(
                "Error saving marketing columns to Cloudflare KV:",
                error
              );
            });

          saveCountersToCloudflare(newCounters).catch((error) => {
            console.error("Error saving counters to Cloudflare KV:", error);
          });
        }

        return {
          marketingColumns: newMarketingColumns,
          counters: newCounters,
          tokens: newTokens,
          activeFilter:
            state.activeFilter?.column === columnName
              ? null
              : state.activeFilter,
        };
      });
    },

    // Method to update counters
    updateCounters: () => {
      set((state) => {
        const newCounters = calculateCounters(
          state.tokens,
          state.marketingColumns
        );

        // Save to Cloudflare KV
        if (isConfigured()) {
          saveCountersToCloudflare(newCounters).catch((error) => {
            console.error("Error saving counters to Cloudflare KV:", error);
          });
        }

        return { counters: newCounters };
      });
    },

    // Method to filter by marketing entry
    filterByMarketingEntry: (column: string, entry: string) => {
      set((state) => ({
        activeFilter: { column, entry },
      }));
    },

    // Filter state
    activeFilter: null,
    setActiveFilter: (filter) => set({ activeFilter: filter }),

    // Sorting state
    sortColumn: null,
    sortDirection: null,
    setSorting: (column, direction) =>
      set({ sortColumn: column, sortDirection: direction }),

    // Selection state
    selectedTokenIds: [],
    toggleTokenSelection: (tokenId) => {
      set((state) => {
        const isSelected = state.selectedTokenIds.includes(tokenId);
        const newSelectedTokenIds = isSelected
          ? state.selectedTokenIds.filter((id) => id !== tokenId)
          : [...state.selectedTokenIds, tokenId];

        return { selectedTokenIds: newSelectedTokenIds };
      });
    },
    selectAllTokens: () => {
      set((state) => ({
        selectedTokenIds: state.tokens.map((token) => token.id),
      }));
    },
    clearTokenSelection: () => {
      set({ selectedTokenIds: [] });
    },

    // Method to get filtered counters based on selection
    getFilteredCounters: () => {
      const { tokens, selectedTokenIds, marketingColumns } = get();
      return calculateFilteredCounters(
        tokens,
        selectedTokenIds,
        marketingColumns
      );
    },
  };
});
